import { Component, ErrorInfo, ReactNode } from 'react';
import { datadogLogs } from '@datadog/browser-logs';

export interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * Optional label used when logging caught errors (e.g. route path or feature name).
   */
  scope?: string;
  /**
   * Optional custom fallback. Can be a ReactNode or a render-prop receiving the error
   * and a `reset` callback that clears the boundary state.
   */
  fallback?: ReactNode | ((args: { error: Error; reset: () => void }) => ReactNode);
  /**
   * Called whenever an error is caught. Useful for tests and additional side-effects.
   */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const { scope, onError } = this.props;
    try {
      datadogLogs.logger.error('React ErrorBoundary caught error', {
        scope,
        error: {
          name: error.name,
          message: error.message,
          stack: error.stack
        },
        componentStack: info.componentStack
      });
    } catch {
      // Never let logging failures escape the boundary.
    }
    onError?.(error, info);
  }

  reset = () => {
    this.setState({ error: null });
  };

  reload = () => {
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    const { children, fallback } = this.props;

    if (!error) {
      return children;
    }

    if (typeof fallback === 'function') {
      return fallback({ error, reset: this.reset });
    }

    if (fallback !== undefined) {
      return fallback;
    }

    return (
      <div
        data-testid="error-boundary-fallback"
        role="alert"
        className="flex flex-col items-center justify-center min-h-[40vh] p-8 text-center">
        <h2 className="text-xl font-semibold text-grey-9 mb-2">Something went wrong</h2>
        <p className="text-sm text-grey-6 mb-6 max-w-md">
          An unexpected error occurred while rendering this page. The team has been notified.
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={this.reset}
            className="px-4 py-2 rounded-md border border-grey-3 text-sm text-grey-9 hover:bg-grey-1">
            Try again
          </button>
          <button
            type="button"
            onClick={this.reload}
            className="px-4 py-2 rounded-md bg-indigo text-sm text-white hover:bg-indigo-mid">
            Reload page
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
